import { skirtingRoomRuns, selectedSkirtingQuantity } from './proposal-skirting.js';
import { scopedFloorArea, accessoryFloorScopes } from './proposal-accessory-scope.js';
import { removalGroups } from './proposal-removal.js';
import { drawnTrims } from './proposal-drawn-trims.js';
/**
 * WHAT THE DRAWING ALREADY MEASURED, for every seed on the screen.
 *
 * Floor area includes the wardrobes (they are floor), the skirting run is the saved net board run for included
 * rooms, the stair steps come from the canvas's own runs — and every one of them is a sum over the
 * assessment, never a number someone typed twice. It lives here, beside the panel that first needed it,
 * so the accessory switchboard under each product reads the SAME numbers rather than a second opinion.
 */
export function workMeasurements(doc) {
    const allRooms = doc.assessment.rooms;
    /** The drawing's own measurement set, when it travelled with the import. */
    const m = doc.assessment.measurements;
    /**
     * ONLY THE ROOMS BEING LAID — the same selection the quote is built from.
     *
     * Jacky, 19 Sep: *"i didnt see your mockup show skirting length, but definately not showing correct in CRM,
     * it says 191.13LM"* — and *"real skirting length is passed from JSON, on those selected room"*. 191.13 LM
     * was the perimeter of EVERY room in the house: the garage, the laundry, the WC, the balcony, the bathroom,
     * the ensuite and three voids that are not being floored and never will be by this job. The drawing gives
     * the run per room; the ones being done come to 108.95 LM.
     *
     * A room is being laid when the canvas gave it a finish and did not stamp it out of scope — the same test
     * the quote uses, so a measurement and a price can never disagree about which rooms are in the job.
     */
    const rooms = allRooms.filter(r => !r.out_of_scope && !r.is_void && (r.quoted_finish || r.flooring));
    // The rooms already carry their wardrobe floor inside `area_m2` — adding `robe_m2` again would
    // double it.
    // The previous aggregate-first rule was wrong: stale whole-site totals can include excluded rooms.
    const floorAreaM2 = scopedFloorArea(doc);
    /**
     * THE SKIRTING RUN COMES FROM THE APP, NOT FROM THE OUTLINES.
     *
     * Jacky: *"read site details, 31.8 GF + 32.8 1F"* — *"fix it"*. A board stops at a doorway jamb, does not
     * run along a stretch already carrying a trim, and is not ordered at all for a room the floor's site
     * register leaves out (her Stair Rises, Kitchen and Landing come to zero). The plan carries the app's
     * figure per floor; summing the room outlines gave 108.95 LM against a true 64.4.
     */
    const skirtingFromApp = m && m.skirting_lm > 0 ? m.skirting_lm : Math.round((doc.assessment.plan ?? []).reduce((n, f) => n + (f.skirting?.skirting_lm ?? 0), 0) * 100) / 100;
    const skirtingRooms = skirtingRoomRuns(doc);
    const hasRoomEvidence = doc.assessment.plan.some(f => f.skirting.rooms.length > 0 || f.skirting.register !== '');
    const skirtingRunM = hasRoomEvidence ? selectedSkirtingQuantity(skirtingRooms) : skirtingFromApp;
    // The authoritative total includes unplaced floor-owned runs that have no room row.
    const drawnTrimM = Math.round((doc.assessment.plan ?? []).flatMap(f => f.trims).reduce((n, t) => n + (t.length_m ?? 0), 0) * 1000) / 1000;
    const scotiaTraces = doc.assessment.plan.flatMap(f => f.trims).filter(t => /scotia/i.test(t.kind));
    const scotiaRunM = scotiaTraces.length && scotiaTraces.every(t => t.measurement_confirmed === true && t.length_m !== null)
        ? Math.round(scotiaTraces.reduce((n, t) => n + (t.length_m ?? 0), 0) * 100) / 100 : undefined;
    const trimRunM = m && m.trim_lm > 0 ? m.trim_lm : drawnTrimM > 0 ? drawnTrimM : Math.round(rooms.reduce((n, r) => n + (r.trims_lm ?? 0), 0) * 1000) / 1000;
    const stairs = doc.assessment.stairs ?? [];
    return {
        floorAreaM2,
        skirtingRunM, skirtingRooms, scotiaRunM,
        trimRunM,
        // Doors the drawing records: the count a door trim is priced on, read from the canvas's own doorways
        // (the reader counts the wall doors it draws), never typed. Counted across the floors being laid —
        // a door into a room nobody is flooring is not a door anyone is trimming off this job.
        doorways: (doc.assessment.doors ?? []).reduce((n, d) => n + (d.count ?? 0), 0),
        stairSteps: stairs.reduce((n, s) => n + (s.steps ?? 0), 0),
        scotiaDrawn: scotiaRunM !== undefined && scotiaRunM > 0,
        underlay: doc.assessment.underlay,
        trimKinds: [...new Set(doc.assessment.plan.flatMap((floor) => floor.trims.map((trim) => trim.kind)).filter(Boolean))],
        floors: accessoryFloorScopes(doc),
        removalGroups: removalGroups(doc),
        drawnTrims: drawnTrims(doc),
    };
}
//# sourceMappingURL=proposal-work-measurements.js.map